import {
  buildLogbuchPdfHeader,
  buildLogbuchPdfStyles,
} from './logbuchPdfBuilder';
import { formatBerlinDate, formatBerlinDateTime } from './pdfDateTime';

function formatDispositionUrgency(value) {
  if (value === 'niedrig') {
    return 'HöRett nicht alarmiert';
  }

  if (value === 'mittel') {
    return 'HöRett verzögert alarmiert';
  }

  if (value === 'hoch') {
    return 'Alarmierung HöRett verweigert';
  }

  return '-';
}

function countAssets(assets = [], kind) {
  return assets.filter((asset) => asset.asset_kind === kind).length;
}

function buildAttachmentSummary(entry) {
  const images = countAssets(entry.assets, 'image');
  const videos = countAssets(entry.assets, 'video');
  const documents = countAssets(entry.assets, 'document');
  return `Bilder: ${images} | Videos: ${videos} | Dokumente: ${documents}`;
}

function buildFilterSummary(filters = {}) {
  const yearText = Array.isArray(filters.yearLabels) && filters.yearLabels.length
    ? filters.yearLabels.join(', ')
    : 'Alle Jahre';
  const urgencyText = Array.isArray(filters.urgencyLabels) && filters.urgencyLabels.length
    ? filters.urgencyLabels.join(', ')
    : 'Alle Alarmierungsbewertungen';

  return `Jahre: ${yearText} | Filter: ${urgencyText}`;
}

function buildSummaryText(entries = []) {
  const notAlerted = entries.filter((entry) => entry.payload?.urgency === 'niedrig').length;
  const delayed = entries.filter((entry) => entry.payload?.urgency === 'mittel').length;
  const refused = entries.filter((entry) => entry.payload?.urgency === 'hoch').length;

  return `Einträge: ${entries.length} | Nicht alarmiert: ${notAlerted} | Verzögert alarmiert: ${delayed} | Alarmierung verweigert: ${refused}`;
}

function buildEntryBlock(entry, index) {
  const infoTable = {
    table: {
      widths: ['26%', '24%', '24%', '26%'],
      body: [
        [
          { text: 'Datum des Einsatzes', bold: true },
          formatBerlinDate(entry.event_date),
          { text: 'Alarmierungsbewertung', bold: true },
          formatDispositionUrgency(entry.payload?.urgency),
        ],
        [
          { text: 'Einsatzleiter', bold: true },
          entry.payload?.deploymentReference || '-',
          { text: 'Zeitvorteil', bold: true },
          `${Number(entry.payload?.estimatedTimeAdvantageMinutes) || 0} Minuten`,
        ],
        [
          { text: 'Anhänge', bold: true },
          { text: buildAttachmentSummary(entry), colSpan: 3 },
          {},
          {},
        ],
        [
          { text: 'Letzte Änderung', bold: true },
          { text: formatBerlinDateTime(entry.updated_at), colSpan: 3 },
          {},
          {},
        ],
      ],
    },
    layout: 'lightHorizontalLines',
  };

  const blockContent = [
    {
      text: `${index + 1}. ${entry.title || 'Ohne Titel'}`,
      style: 'feedbackEntryTitle',
      margin: [0, 0, 0, 6],
    },
    infoTable,
    {
      text: 'Beschreibung der Einsatzsituation und Einsatzoption der Höhenrettung',
      style: 'feedbackSectionLabel',
      margin: [0, 8, 0, 3],
    },
    {
      text: String(entry.payload?.recommendation || '-'),
      style: 'feedbackBody',
      margin: [0, 0, 0, 3],
    },
    {
      text: 'Weshalb wurde die HöRett nicht alarmiert / verzögert alarmiert / verweigert?',
      style: 'feedbackSectionLabel',
      margin: [0, 8, 0, 3],
    },
    {
      text: String(entry.description || '-'),
      style: 'feedbackBody',
      margin: [0, 0, 0, 3],
    },
  ];

  const links = Array.isArray(entry.payload?.links) ? entry.payload.links.filter(Boolean) : [];
  if (links.length) {
    blockContent.push(
      {
        text: 'Links',
        style: 'feedbackSectionLabel',
        margin: [0, 8, 0, 3],
      },
      {
        ul: links.map((link) => ({ text: link, link, color: '#1565C0' })),
        margin: [0, 0, 0, 0],
      },
    );
  }

  return {
    stack: blockContent,
    margin: [0, 0, 0, 16],
  };
}

export function buildDispositionPdfFilename(exportDate = new Date()) {
  const safeDate = formatBerlinDate(exportDate, { fallback: formatBerlinDate(new Date()) });

  return `Disposition-HöRett-Auswertung ${safeDate}.pdf`;
}

export function buildDispositionPdfDocument({
  entries = [],
  filters = {},
  brandingAssets = {},
  exportDate = new Date(),
} = {}) {
  const styles = {
    ...buildLogbuchPdfStyles(),
    feedbackMeta: { fontSize: 10, color: '#4b5563', margin: [0, 0, 0, 6] },
    feedbackEntryTitle: { fontSize: 13, bold: true, color: '#111827' },
    feedbackSectionLabel: { fontSize: 10, bold: true, color: '#374151' },
    feedbackBody: { fontSize: 10, color: '#111827', lineHeight: 1.25 },
    emptyState: { fontSize: 11, italics: true, color: '#6b7280' },
  };

  return {
    pageSize: 'A4',
    pageMargins: [36, 36, 36, 36],
    content: [
      buildLogbuchPdfHeader({ assets: brandingAssets, title: 'Disposition HöRett' }),
      {
        text: 'Export der aktuell sichtbaren Auswertungseinträge. Bilder, Videos und Dokumente werden nicht eingebettet; es werden nur die jeweiligen Anhangszahlen ausgewiesen.',
        style: 'feedbackMeta',
      },
      {
        text: buildFilterSummary(filters),
        style: 'feedbackMeta',
      },
      {
        text: `Exportiert am: ${formatBerlinDateTime(exportDate)}`,
        style: 'feedbackMeta',
      },
      {
        text: buildSummaryText(entries),
        style: 'sum',
        margin: [0, 0, 0, 12],
      },
      ...(entries.length
        ? entries.map((entry, index) => buildEntryBlock(entry, index))
        : [{ text: 'Für die aktuellen Filter sind keine Einträge vorhanden.', style: 'emptyState' }]),
    ],
    styles,
    defaultStyle: {
      fontSize: 10,
    },
  };
}